/**
 * The Przegląd tab's „Rozliczenie" band — T-K1: where the client stands (Saldo), the two acts a
 * trainer takes from the card (Żetony), and the next session on their calendar (Nadchodzące).
 *
 * Drawn only while the trainer bills in credits and this client has a ledger (R18, R16) — not an
 * empty band otherwise. Whether it stands is `model/creditBand`'s `creditBandPresence`; the acts
 * are `features/credit-ledger`'s, the same dialog and the same write the Rozliczenia tab makes.
 *
 * ── It waits with the tab, and holds its room on the policy's word ────────────────────────────
 * The tab's one gate waits for the band's reads too, and the room is held while it waits exactly
 * when the booking policy — read by the route before the first frame — says credits run. A retry
 * after a failure waits on the band's own gate, never the tab's.
 *
 * The act dialog stands OUTSIDE the band, so a band that leaves (credits switched off under an
 * open act) does not take the act with it; the act speaks through the card's one live region
 * (`cardAnnouncer`), which outlives both.
 */

import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { LedgerActDialog, creditActClass, standingUnit } from '~/features/credit-ledger';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import skeleton from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { CreditBandState } from '../lib/useCreditBand';
import eyebrow from './cardEyebrow.module.css';
import styles from './CreditBand.module.css';
import { SectionHead } from './SectionHead';

interface CreditBandProps {
  readonly clientId: number;
  readonly clientName: string;
  readonly band: CreditBandState;
  /** The tab's gate — the band is one of the answers the tab waits for. */
  readonly gate: SkeletonGate;
  /** Open the client's Kalendarz tab. */
  readonly onOpenCalendar: () => void;
  readonly reducedMotion: boolean;
}

const WORDS = 'web:clientDetail.credits';

export function CreditBand({
  clientId,
  clientName,
  band,
  gate,
  onOpenCalendar,
  reducedMotion,
}: CreditBandProps) {
  const { t } = useTranslation();
  const { view, act } = band;
  const again = useSkeletonGate(band.rereading);
  const retryRef = useRef<HTMLButtonElement | null>(null);
  const openRef = useRef<HTMLAnchorElement | null>(null);
  const calendarRef = useRef<HTMLButtonElement | null>(null);
  /** A retry was pressed: when the band has answered, focus goes to what replaced the button. */
  const refocusAfterRead = useRef(false);
  const settled = !gate.waiting && !again.waiting;
  useEffect(() => {
    if (!settled || !refocusAfterRead.current) return;
    refocusAfterRead.current = false;
    (openRef.current ?? retryRef.current)?.focus();
  }, [settled]);
  const waiting = gate.waiting || again.waiting;
  const bars = gate.skeleton || again.skeleton;
  const reveal = `${gate.reveal} ${again.reveal}`;
  const drawn = waiting ? band.reserved : view.kind !== 'off';
  const bar = String(skeleton.bar);

  return (
    <>
      {drawn ? (
        <div className={styles.band}>
          <SectionHead title={t(`${WORDS}.title`)} reducedMotion={reducedMotion} />
          <section className={styles.card} aria-label={t(`${WORDS}.title`)} aria-busy={waiting}>
            {waiting ? (
              <>
                {[0, 1, 2].map((column) => (
                  <div key={column} className={styles.column} aria-hidden="true">
                    {bars ? (
                      <>
                        <i className={`${bar} ${String(styles.barEyebrow)}`} />
                        <i className={`${bar} ${String(styles.barLine)}`} />
                        <i className={`${bar} ${String(styles.barShort)}`} />
                      </>
                    ) : null}
                  </div>
                ))}
                <span className={hidden.srOnly}>{t(`${WORDS}.loading`)}</span>
              </>
            ) : view.kind === 'failed' ? (
              <div className={`${String(styles.failed)} ${reveal}`} role="alert">
                <span>{t(`${WORDS}.failed`)}</span>
                <button
                  ref={retryRef}
                  type="button"
                  className={creditActClass}
                  onClick={() => {
                    // The button leaves with the wait; focus comes back when the band has answered.
                    refocusAfterRead.current = true;
                    band.retry();
                  }}
                >
                  {t(`${WORDS}.retry`)}
                </button>
              </div>
            ) : view.kind === 'ready' ? (
              <>
                <div className={`${String(styles.column)} ${reveal}`}>
                  <em className={eyebrow.eyebrow}>{t(`${WORDS}.balance`)}</em>
                  <div className={styles.figure}>
                    <b className={styles.figureValue}>{view.standing.count}</b>
                    <span className={styles.figureWords}>
                      <b className={styles.figureUnit}>{standingUnit(view.standing, t)}</b>
                      {view.since === null ? null : (
                        <span className={styles.note}>{view.since}</span>
                      )}
                    </span>
                  </div>
                  <Link
                    ref={openRef}
                    className={`${String(inlineButton.inlineTextButton)} ${String(styles.link)}`}
                    to="/rezerwacje"
                    search={{ tab: 'rozliczenia', klient: clientId }}
                  >
                    {t(`${WORDS}.open`)}
                  </Link>
                </div>

                <div className={`${String(styles.column)} ${reveal}`}>
                  <em className={eyebrow.eyebrow}>{t(`${WORDS}.tokens`)}</em>
                  <div className={styles.acts}>
                    <button
                      type="button"
                      className={creditActClass}
                      onClick={() => {
                        act.open('grant');
                      }}
                    >
                      {t('web:booking.trainer.credits.acts.grant')}
                    </button>
                    <button
                      type="button"
                      className={creditActClass}
                      onClick={() => {
                        // With nothing owed it still opens: its dialog says why it cannot be sent.
                        act.open('settlement');
                      }}
                    >
                      {t(`${WORDS}.settle`)}
                    </button>
                  </div>
                  <span className={styles.note}>{t(`${WORDS}.noteVisible`)}</span>
                </div>

                <div className={`${String(styles.column)} ${reveal}`}>
                  <em className={eyebrow.eyebrow}>{t(`${WORDS}.upcoming`)}</em>
                  {view.next.kind === 'session' ? (
                    <>
                      <b className={styles.when}>{view.next.when}</b>
                      <span className={styles.what}>{view.next.what}</span>
                    </>
                  ) : view.next.kind === 'none' ? (
                    <span className={styles.what}>{t(`${WORDS}.nothingAhead`)}</span>
                  ) : band.rereadingNext ? (
                    <span className={styles.what} role="status">
                      {t('web:booking.credits.act.loadingSessions')}
                    </span>
                  ) : (
                    <span className={styles.what} role="alert">
                      {t(`${WORDS}.nextFailed`)}{' '}
                      <button
                        type="button"
                        className={`${String(inlineButton.inlineTextButton)} ${String(styles.link)}`}
                        onClick={() => {
                          // The retry leaves with the failure; the column's own link stands.
                          band.retrySchedule();
                          calendarRef.current?.focus();
                        }}
                      >
                        {t(`${WORDS}.retry`)}
                      </button>
                    </span>
                  )}
                  <button
                    ref={calendarRef}
                    type="button"
                    className={`${String(inlineButton.inlineTextButton)} ${String(styles.link)}`}
                    onClick={onOpenCalendar}
                  >
                    {t(`${WORDS}.calendar`)}
                  </button>
                </div>
              </>
            ) : null}
          </section>
        </div>
      ) : null}

      <LedgerActDialog
        draft={act.draft}
        clientName={clientName}
        standing={
          view.kind === 'ready'
            ? { balance: view.balance, sessionsOwed: view.sessionsOwed }
            : { balance: 0, sessionsOwed: 0 }
        }
        sending={act.sending}
        locked={act.unanswered}
        refusal={act.refusal}
        onEdit={act.edit}
        onSend={act.send}
        onClose={act.close}
      />
    </>
  );
}
